// @flow

import React, { useEffect, useState, useRef } from 'react'
import { withRouter } from 'react-router-dom'
import glamorous from 'glamorous'
import getContent from 'pp-react-l10n'
import { Link } from '@paypalcorp/pp-react'
import clientData from 'utils/client-data'
import { getProfilePhotoUrl } from 'utils/photo'
import { getDisplayName } from 'utils/name'
import { getSendUrl, getRequestUrl, getReportLink } from 'utils/generate-urls'
import { extractAmountInfo } from 'screens/recipient-slug/extract-amount-info'
import {
  getAmountFieldInfo,
  getFormattedValue,
  getFormattedAmount,
  normalizeNumber,
} from 'screens/shared/amount-utils'

import Events from 'lib/analytics/event-tags'
import { trackImpression, trackLink, tracking } from 'lib/analytics'

import { INSTRUMENTATION_MAP } from 'utils/constants'

import CoverPhoto from 'screens/shared/components/cover-photo'
import ProfileCard from 'screens/shared/components/profile-card'

const INSTRUMENTATION = INSTRUMENTATION_MAP.recipient

const l10n = getContent('pages/shared')

const UTM_TAGS = clientData.utmTags
const PARTNER_TAGS = clientData.partnerTags

const Container = glamorous.div({
  margin: '2.25rem 0 2rem',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
})

const CoverContainer = glamorous.div({
  zIndex: 0,
  marginBottom: '-6.375rem',
})

const ProfileCardContainer = glamorous.div({
  zIndex: 1,
})

const ReportContainer = glamorous.div({
  marginTop: '3rem',
})
const ReportLink = glamorous.a({ fontSize: '0.875rem' })

type Props = {
  slugDetails: SlugDetails,
  match: Match,
}

function SlugRecipientProfile(props: Props) {
  useEffect(() => {
    trackImpression(
      INSTRUMENTATION.OLD.PAGE_GROUP,
      INSTRUMENTATION.OLD.PAGE_NAME,
      { ...UTM_TAGS, ...PARTNER_TAGS },
    )
    tracking(
      Events.public_identity_profile_screen_shown({
        ...UTM_TAGS,
        ...PARTNER_TAGS,
      }),
    )
  }, [])

  const [reportLink] = useState(() => getReportLink())
  const { current: isUserLoggedIn } = useRef(clientData.isUserLoggedIn)

  const {
    match: { params: { amountInfo } = {} } = {},
    slugDetails = {},
    slugDetails: {
      paypalmeSlugName = '',
      userInfo: {
        profilePhotoUrl = '',
        displayName = '',
        displayAddress = '',
      } = {},
      personalizationSettings: {
        coverPhotoUrl = '',
        coverPhotoVerticalPan = '50',
        welcomeNote = '',
      } = {},
    } = {},
  } = props

  const [{ amount, currency: currencyCode } = {}] = useState(() =>
    extractAmountInfo(amountInfo),
  )
  const showAmount =
    clientData.isProfileAmountTreatment && !isNaN(amount) && amount > 0
  const [{ currencyDecimal, currencySymbolMap } = {}] = useState(() =>
    getAmountFieldInfo(),
  )
  const [formattedAmount] = useState(() =>
    getFormattedAmount({
      amount,
      currencyCode,
      symbolIsoCurrency: true,
    }),
  )

  const handleSendReqUrl = async type => {
    trackLink(
      type,
      INSTRUMENTATION.OLD.PAGE_GROUP,
      INSTRUMENTATION.OLD.PAGE_NAME,
      'link',
    )
    let redirectUrl

    if (type === 'send') {
      tracking(Events.public_identity_profile_send_pressed({}))

      redirectUrl = getSendUrl({
        isLoggedIn: isUserLoggedIn,
        slugDetails,
        ...(showAmount && {
          amount: String(normalizeNumber(amount)),
          currencyCode,
        }),
      })
    } else {
      tracking(Events.public_identity_profile_request_pressed({}))

      redirectUrl = getRequestUrl({
        isLoggedIn: isUserLoggedIn,
        slugDetails,
        ...(showAmount && {
          amount: String(normalizeNumber(amount)),
          currencyCode,
        }),
      })
    }

    if (type === 'request' || !clientData.isPSD2Treatment) {
      window.location.assign(redirectUrl)
      return
    }

    const body = {
      redirectUrl,
      slugDetails,
      isUserLoggedIn,
    }

    if (showAmount) {
      body.amount = String(normalizeNumber(amount))
      body.currencyCode = currencyCode
    }

    const response = await fetch(`${clientData.requestURI}/v1/guest`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-TOKEN': clientData.csrf,
      },
      body: JSON.stringify(body),
    })

    try {
      const data = await response.json()

      if (response.status >= 400) {
        window.location.assign(redirectUrl)
        return
      }

      window.location.assign(data.loginUrl)
      return
    } catch (err) {
      window.location.assign(redirectUrl)
    }
  }

  const onReport = () => {
    trackLink(
      'report',
      INSTRUMENTATION.OLD.PAGE_GROUP,
      INSTRUMENTATION.OLD.PAGE_NAME,
      'link',
    )
    tracking(Events.public_identity_profile_report_pressed({}))
  }

  return (
    <Container>
      <CoverContainer>
        <CoverPhoto
          photoUrl={coverPhotoUrl}
          photoVerticalPan={coverPhotoVerticalPan}
        />
      </CoverContainer>
      <ProfileCardContainer>
        <ProfileCard
          photoUrl={getProfilePhotoUrl(profilePhotoUrl)}
          displayName={getDisplayName(displayName)}
          handle={`@${paypalmeSlugName}`}
          displayLocation={displayAddress}
          blurb={welcomeNote}
          showActions={true}
          onSend={() => handleSendReqUrl('send')}
          onRequest={() => handleSendReqUrl('request')}
          showAmount={showAmount}
          amount={amount}
          currencyCode={currencyCode}
          currencyDecimal={currencyDecimal}
          currencySymbolMap={currencySymbolMap}
          formatAmount={(amount: string) =>
            getFormattedValue({ amount, currencyCode })
          }
          amountLabel={formattedAmount}
        />
      </ProfileCardContainer>
      <ReportContainer>
        <Link
          as={ReportLink}
          secondary
          target="_blank"
          rel="noopener noreferrer"
          href={reportLink}
          onClick={onReport}
        >
          {l10n('button.report')}
        </Link>
      </ReportContainer>
    </Container>
  )
}

export default withRouter(SlugRecipientProfile)
